import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { toast } from 'react-toastify';

import api from '../../../services/api';
import { ApplicationState } from '../../../store';
import { SubscriptionInfo } from '../../../store/modules/user/types';
import {
  Container,
  PlansGrid,
  PlanCard,
  PlanCardHighlight,
  Badge,
  PriceText,
  FeatureList,
} from './PlansStyles';

interface PlanDef {
  id: string;
  name: string;
  priceId: string;
  tradeLimit: number;
  price: number;
  features: string[];
}

const planFeatures: Record<string, string[]> = {
  basic: [
    'Até 100 trades/mês',
    '4 insights IA/mês',
    'Dashboard completo',
    'Histórico de trades',
    'Exportação CSV',
    'Notificações Stop Loss / Stop Win',
  ],
  pro: [
    'Até 500 trades/mês',
    '8 insights IA/mês',
    'Dashboard completo',
    'Histórico de trades',
    'Exportação CSV',
    'Notificações Stop Loss / Stop Win',
  ],
  premium: [
    'Trades ilimitados',
    '20 insights IA/mês',
    'Dashboard completo',
    'Histórico de trades',
    'Exportação CSV',
    'Notificações Stop Loss / Stop Win',
  ],
};

function formatPrice(centavos: number): string {
  return `R$ ${(centavos / 100).toFixed(2).replace('.', ',')}`;
}

const planOrder = ['basic', 'pro', 'premium'];

function checkIsTrialing(user: { trial?: { active: boolean }; subscription?: SubscriptionInfo }): boolean {
  if (user.trial && user.trial.active) return true;
  if (!user.subscription) return false;
  if (user.subscription.status !== 'trialing') return false;
  if (!user.subscription.currentPeriodEnd) return false;
  return new Date(user.subscription.currentPeriodEnd) > new Date();
}

function checkHasAccess(user: { isActive: boolean; trial?: { active: boolean }; subscription?: SubscriptionInfo }): boolean {
  if (user.isActive) return true;
  if (checkIsTrialing(user)) return true;
  const status = user.subscription && user.subscription.status;
  return status === 'active' || status === 'past_due';
}

function checkTrialExpired(user: { isActive: boolean; trial?: { active: boolean }; subscription?: SubscriptionInfo }): boolean {
  if (checkHasAccess(user)) return false;
  if (!user.subscription) return false;
  const status = user.subscription.status;
  return status !== 'active' && status !== 'past_due';
}

export default function Plans() {
  const [loading, setLoading] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<PlanDef | null>(null);
  const user = useSelector((state: ApplicationState) => state.user.user);
  const subscription = user.subscription;
  const currentPlan = subscription && subscription.plan !== 'free' ? subscription.plan : null;

  const trialExpired = checkTrialExpired(user);

  const plans: PlanDef[] = [
    {
      id: 'basic',
      name: 'Básico',
      priceId: '',
      tradeLimit: 100,
      price: 2990,
      features: planFeatures.basic,
    },
    {
      id: 'pro',
      name: 'Pro',
      priceId: '',
      tradeLimit: 500,
      price: 5990,
      features: planFeatures.pro,
    },
    {
      id: 'premium',
      name: 'Premium',
      priceId: '',
      tradeLimit: -1,
      price: 9990,
      features: planFeatures.premium,
    },
  ];

  function getCurrentPlanDef(): PlanDef | undefined {
    return plans.find(p => p.id === currentPlan);
  }

  function isUpgrade(targetPlanId: string): boolean {
    return planOrder.indexOf(targetPlanId) > planOrder.indexOf(currentPlan || '');
  }

  function handlePlanClick(plan: PlanDef) {
    if (currentPlan) {
      setConfirmModal(plan);
    } else {
      handleSubscribe(plan.id);
    }
  }

  async function handleSubscribe(planId: string) {
    setConfirmModal(null);
    setLoading(planId);
    try {
      const plansRes = await api.get('/billing/plans');
      const serverPlan = plansRes.data.find((p: any) => p.id === planId);
      if (!serverPlan) {
        toast.error('Plano não encontrado');
        setLoading(null);
        return;
      }

      const res = await api.post('/billing/checkout', {
        priceId: serverPlan.priceId,
      });
      if (res.data.updated) {
        toast.success('Plano alterado com sucesso!');
        setLoading(null);
        window.location.href = '/';
        return;
      }
      window.open(res.data.sessionUrl, '_blank');
    } catch (err) {
      const e = err as any;
      const msg = (e && e.response && e.response.data && e.response.data.error) || 'Erro ao iniciar checkout';
      toast.error(msg);
      setLoading(null);
    }
  }

  function renderConfirmModal() {
    if (!confirmModal || !currentPlan) return null;

    const current = getCurrentPlanDef();
    if (!current) return null;

    const upgrade = isUpgrade(confirmModal.id);
    const priceDiff = confirmModal.price - current.price;

    return (
      <Dialog
        open={true}
        onClose={() => setConfirmModal(null)}
        PaperProps={{
          style: {
            background: '#2C325C',
            color: '#fff',
            maxWidth: '440px',
          }
        }}
      >
        <DialogTitle style={{ paddingBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {upgrade
              ? <ArrowUpwardIcon style={{ color: '#4BDC4B' }} />
              : <ArrowDownwardIcon style={{ color: '#ffee58' }} />
            }
            <Typography variant="h6" style={{ color: '#fff', fontWeight: 700 }}>
              {upgrade ? 'Upgrade de plano' : 'Downgrade de plano'}
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            margin: '16px 0',
          }}>
            <div style={{ textAlign: 'center' }}>
              <Typography variant="body2" style={{ color: '#ffffff99' }}>Atual</Typography>
              <Typography variant="h6" color="primary" style={{ fontWeight: 700 }}>
                {current.name}
              </Typography>
              <Typography variant="body2" style={{ color: '#ffffffcc' }}>
                {formatPrice(current.price)}/mês
              </Typography>
            </div>
            <Typography variant="h5" style={{ color: '#ffffff66' }}>→</Typography>
            <div style={{ textAlign: 'center' }}>
              <Typography variant="body2" style={{ color: '#ffffff99' }}>Novo</Typography>
              <Typography variant="h6" color="primary" style={{ fontWeight: 700 }}>
                {confirmModal.name}
              </Typography>
              <Typography variant="body2" style={{ color: '#ffffffcc' }}>
                {formatPrice(confirmModal.price)}/mês
              </Typography>
            </div>
          </div>

          <div style={{
            background: '#1E2141',
            borderRadius: '8px',
            padding: '16px',
            marginTop: '16px',
          }}>
            {upgrade ? (
              <>
                <Typography variant="body2" style={{ color: '#ffffffcc', marginBottom: '8px' }}>
                  <strong style={{ color: '#4BDC4B' }}>Cobrança proporcional:</strong> Será cobrada a diferença de {formatPrice(priceDiff)} proporcional aos dias restantes do período atual.
                </Typography>
                <Typography variant="body2" style={{ color: '#ffffffcc' }}>
                  <strong style={{ color: '#ffee58' }}>Próxima cobrança:</strong> A partir da próxima renovação, o valor será de {formatPrice(confirmModal.price)}/mês.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" style={{ color: '#ffffffcc', marginBottom: '8px' }}>
                  <strong style={{ color: '#ffee58' }}>Crédito proporcional:</strong> Você receberá um crédito de {formatPrice(Math.abs(priceDiff))} proporcional aos dias restantes, que será aplicado na próxima cobrança.
                </Typography>
                <Typography variant="body2" style={{ color: '#ffffffcc' }}>
                  <strong style={{ color: '#ffee58' }}>Próxima cobrança:</strong> A partir da próxima renovação, o valor será de {formatPrice(confirmModal.price)}/mês.
                </Typography>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setConfirmModal(null)}
            style={{ color: '#ffffffcc' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSubscribe(confirmModal.id)}
            disabled={loading !== null}
            style={{ color: '#1E2141', fontWeight: 700 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirmar alteração'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Container>
        {trialExpired && (
          <Typography
            variant="body1"
            style={{
              color: '#ffee58',
              marginBottom: '16px',
              background: '#625c27',
              padding: '12px 16px',
              borderRadius: '8px',
            }}
          >
            Seu trial de 7 dias expirou. Escolha um plano para continuar.
          </Typography>
        )}
        <Typography variant="h4" color="primary" style={{ fontWeight: 700 }}>
          Escolha seu plano
        </Typography>
        <Typography
          variant="body1"
          style={{ color: '#ffffffcc', marginTop: '8px' }}
        >
          Assine e tenha acesso completo ao DashTrader
        </Typography>

        <PlansGrid>
          {plans.map(plan => {
            const isCurrentPlan = currentPlan === plan.id;
            const CardComponent =
              isCurrentPlan ? PlanCardHighlight : (!currentPlan && plan.id === 'pro') ? PlanCardHighlight : PlanCard;
            return (
              <CardComponent key={plan.id} style={isCurrentPlan ? { border: '2px solid #4BDC4B' } : {}}>
                {isCurrentPlan && <Badge style={{ background: '#4BDC4B' }}>PLANO ATUAL</Badge>}
                {!currentPlan && plan.id === 'pro' && <Badge>MAIS POPULAR</Badge>}
                <Typography
                  variant="h5"
                  style={{ fontWeight: 700, color: '#fff' }}
                >
                  {plan.name}
                </Typography>
                <PriceText>
                  <Typography
                    variant="h4"
                    color="primary"
                    style={{ fontWeight: 700 }}
                  >
                    {formatPrice(plan.price)}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: '#ffffff99' }}
                  >
                    /mês
                  </Typography>
                </PriceText>
                <Typography
                  variant="body2"
                  style={{ color: '#ffffffcc' }}
                >
                  {plan.tradeLimit === -1
                    ? <span>Trades <b>ilimitados</b></span>
                    : <span>Até <b>{plan.tradeLimit}</b> trades/mês</span>}
                </Typography>
                <FeatureList>
                  {plan.features.map((feat, i) => (
                    <div key={i}>
                      <CheckIcon
                        style={{ color: '#4BDC4B', fontSize: '18px' }}
                      />
                      {feat}
                    </div>
                  ))}
                </FeatureList>
                <Button
                  variant={isCurrentPlan ? 'outlined' : 'contained'}
                  color="primary"
                  fullWidth
                  onClick={() => handlePlanClick(plan)}
                  disabled={loading !== null || isCurrentPlan}
                  style={{
                    padding: '10px',
                    fontWeight: 700,
                    color: isCurrentPlan ? '#4BDC4B' : '#1E2141',
                    borderColor: isCurrentPlan ? '#4BDC4B' : undefined,
                  }}
                >
                  {loading === plan.id ? (
                    <CircularProgress size={24} />
                  ) : isCurrentPlan ? (
                    'Plano atual'
                  ) : currentPlan ? (
                    isUpgrade(plan.id) ? 'Upgrade' : 'Downgrade'
                  ) : (
                    'Assinar'
                  )}
                </Button>
              </CardComponent>
            );
          })}
        </PlansGrid>
      </Container>
      {renderConfirmModal()}
    </>
  );
}
